/**
 * Supplements tracker — pure helpers (sibling of nutrition.ts / hydration.ts).
 *
 * Supplements are an ADHERENCE tracker: a curated stack (name + dose + time
 * slot) checked off daily. XP is daily-capped adherence (taken / scheduled), so
 * it's farm-proof and rewards consistency. `sumSupplementXP` is the single
 * definition, crediting the Personal domain once and feeding the Supplements
 * area rank.
 */

import { skippedCount, takenCount, type IntakeSchedule, type PauseSpan, type TimeOfDay } from './intakeSchedule';

export type SupplementSlot = TimeOfDay;

/**
 * A supplement. The schedule fields (strength, amount, times, how often, dates, changes,
 * supply — `utils/intakeSchedule.ts`) are optional: a list saved before them is one dose a
 * day in its `slot`, keyed by its id, exactly as before.
 */
export interface Supplement extends IntakeSchedule {
  id: string;
  name: string;
  dose: string;
  icon: string;
  slot: SupplementSlot;
  active: boolean;
  /**
   * When it was paused and resumed (`pauseItem` / `resumeItem` — the stores' Pause and Resume),
   * so a paused item's days before its pause still count. Never edited by a form.
   */
  pauses?: PauseSpan[] | null;
}

/**
 * One day's record: the dose keys taken (and `~<key>` for each one skipped) + how many doses
 * were due that day (a snapshot, so past adherence stays right as the list changes).
 */
export interface SupplementDay {
  date: string; // local YYYY-MM-DD
  takenIds: string[];
  scheduled: number;
}

export const SUPPLEMENT_SLOTS: { id: SupplementSlot; label: string }[] = [
  { id: 'morning', label: 'Morning' },
  { id: 'afternoon', label: 'Afternoon' },
  { id: 'evening', label: 'Evening' },
  { id: 'bedtime', label: 'Bedtime' },
];

import { POINTS } from './levelCalculation';

/** Icon keys offered by the stack icon picker (mapped to lucide in the UI). */
export const SUPPLEMENT_ICON_KEYS = [
  'pill', 'tablets', 'capsule', 'droplet', 'leaf', 'citrus', 'flask', 'bone',
  'zap', 'heart', 'brain', 'dumbbell', 'fish', 'sun', 'sparkles', 'coffee',
] as const;

export const DEFAULT_SUPPLEMENT_ICON = 'pill';

/** Economy XP for a fully-adherent supplement day (tunable; matches the others). */
export const DAILY_SUPPLEMENT_XP = 50;

/** A small starter stack, seeded on first use; fully user-editable. */
export const DEFAULT_STACK: Supplement[] = [
  { id: 'supp-d3', name: 'Vitamin D3', dose: '2000 IU', icon: 'sun', slot: 'morning', active: true },
  { id: 'supp-omega', name: 'Omega-3', dose: '1000 mg', icon: 'fish', slot: 'morning', active: true },
  { id: 'supp-mag', name: 'Magnesium', dose: '400 mg', icon: 'leaf', slot: 'bedtime', active: true },
];

/** 0..100 adherence for a (taken, scheduled) pair. */
export function adherencePct(taken: number, scheduled: number): number {
  if (scheduled <= 0) return 0;
  return Math.min(100, Math.max(0, (taken / scheduled) * 100));
}

/** Economy XP for ONE day — proportional to adherence, capped at 100%. */
export function dayAdherenceXP(takenCount: number, scheduled: number): number {
  if (scheduled <= 0) return 0;
  const ratio = Math.min(1, Math.max(0, takenCount) / scheduled);
  // Config-backed cap (console → XP economy); the const above is the default.
  return Math.round(ratio * (POINTS.TRACKER_SUPPLEMENTS_DAILY ?? DAILY_SUPPLEMENT_XP));
}

/** Total Supplements economy XP across days (the single source of truth). */
export function sumSupplementXP(days: ReadonlyArray<SupplementDay>): number {
  return days.reduce((sum, d) => sum + dayAdherenceXP(takenCount(d.takenIds), d.scheduled), 0);
}

/**
 * A day is "fully adherent" once every due dose is recorded and at least one was taken. A
 * deliberate skip doesn't break it (it earns nothing — XP counts taken only); a dose left
 * unrecorded does.
 */
export function isFullyAdherent(day: SupplementDay): boolean {
  const taken = takenCount(day.takenIds);
  return day.scheduled > 0 && taken > 0 && taken + skippedCount(day.takenIds) >= day.scheduled;
}

/**
 * Consecutive fully-adherent days ending at `todayStr`. An in-progress today
 * that isn't complete yet doesn't break the streak (mirrors the other trackers).
 * Reuses the pure date math from hydration.ts.
 *
 * A day with NOTHING due (a once-a-week supplement's other days) neither breaks nor extends
 * it: a row due 0, or — with `dueOn` (the schedule's count for a day with no row) — a day
 * where nothing was due. Without `dueOn` a day with no row breaks it, as it always has.
 */
export function supplementStreak(
  days: ReadonlyArray<SupplementDay>,
  todayStr: string,
  shift: (ymd: string, n: number) => string,
  dueOn?: (date: string) => number
): number {
  const byDate = new Map<string, SupplementDay>();
  for (const d of days) byDate.set(d.date, d);
  const earliest = days.reduce((min, d) => (d.date < min ? d.date : min), todayStr);
  /** true = counts, false = breaks, null = nothing was due (passed over). */
  const state = (date: string): boolean | null => {
    const d = byDate.get(date);
    if (d) return d.scheduled > 0 ? isFullyAdherent(d) : null;
    if (dueOn && dueOn(date) === 0) return null;
    return false;
  };

  let streak = 0;
  let cursor = state(todayStr) === true ? todayStr : shift(todayStr, -1);
  // Bounded: before the earliest record there is nothing to count.
  while (cursor >= earliest) {
    const s = state(cursor);
    if (s === false) break;
    if (s === true) streak++;
    cursor = shift(cursor, -1);
  }
  return streak;
}
